import React, { useMemo } from 'react';
import { orderBy, startCase } from 'lodash';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import Body2 from '@joinforma/ui/components/Body2';
import Divider from '@joinforma/ui/components/Divider';
import Title2 from '@joinforma/ui/components/Title2';

import SideNavigationBar from 'components/common/SideNavigationBar';
import SideLink from 'components/common/SideLink';

import { getWalletDisplayName } from 'utils/company';
import date_helper from 'utils/date-helper';
import { StoreRoutes } from 'utils/routeGenerator';
import {
  getPretaxAccountLabel,
  translateWalletTypeToDisplay,
} from 'utils/pretaxAccount';

import {
  PRETAX_ACCOUNT_TYPES,
  PRETAX_ACCOUNT_V2_TYPES,
} from 'constants/accounts';
import { STORE_LAYOUT_ELEMENTS } from 'constants/testSelectors';

import { PretaxWallet } from 'contexts/PretaxContext/types';

import { EmployeeWallet, Category, PretaxAccount } from './types';

const Container = styled.div.attrs({ role: 'navigation' })`
  width: 182px;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 16px;
`;

const AccountContainer = styled.div`
  margin-left: -13px;
`;

const PreTaxAccountContainer = styled.div`
  margin-top: -4px;
`;

const Section = styled.div``;

const HorizontalSeparator = styled.div`
  width: 147px;
  margin-top: 12px;
  margin-bottom: 24px;
`;

const SuggestionContainer = styled.div`
  width: 175px;
  height: 182px;
`;

const SuggestionQuestion = styled(Title2)`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const FormLinkContainer = styled.div`
  margin-top: 13px;
  width: 143px;
`;

const FormLink = styled.a.attrs({ target: '_blank' })`
  all: unset;
`;

const FormLinkText = styled(Body2)`
  color: ${({
    theme: {
      colors: {
        text: { link },
      },
    },
  }) => link};
`;

const FormText = styled(Body2)`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const CategoryContainer = styled.div`
  margin-left: -13px;
`;

interface Props {
  employee_wallets: Array<EmployeeWallet>;
  filtered_wallet?: EmployeeWallet;
  categories_with_vendors: Array<Category>;
  pretax_accounts: Array<PretaxAccount> | Array<PretaxWallet>;
  has_fsa_and_lpf?: boolean;
  is_forma_pretax?: boolean;
}

const CategoryFilters = ({
  employee_wallets,
  filtered_wallet,
  categories_with_vendors,
  pretax_accounts,
  has_fsa_and_lpf = false,
  is_forma_pretax = false,
}: Props) => {
  const location = useLocation();
  const { year } = queryString.parse(location.search);
  const { company_wallet_configuration } = filtered_wallet || {};

  const account_type = useMemo(() => {
    const account = pretax_accounts[0];

    if (account && !is_forma_pretax) {
      // Alegeus combined LPFSA & HSA
      if (has_fsa_and_lpf) {
        return 'LPFSA & HSA';
      }

      // Alegeus pretax accounts
      if (Object.hasOwn(account, 'account_type')) {
        return (account as PretaxAccount).account_type;
      }
    }

    return '';
  }, [pretax_accounts, has_fsa_and_lpf, is_forma_pretax]);

  const renderSideLinks = () => {
    // Render single link for Alegeus pretax store
    if (!is_forma_pretax) {
      return has_fsa_and_lpf ? (
        <SideLink
          key="LPFSAHSA"
          route={StoreRoutes.detailPretax({
            pretax_account: PRETAX_ACCOUNT_TYPES.LPFSAHSA,
          })}
        >
          {'LPFSA & HSA'}
        </SideLink>
      ) : (
        <SideLink
          key={getPretaxAccountLabel({ account_type })}
          route={StoreRoutes.detailPretax({
            pretax_account: getPretaxAccountLabel({
              account_type,
            }),
          })}
        >
          {getPretaxAccountLabel({ account_type })}
        </SideLink>
      );
    }

    // Render separate link(s) for each 2.0 pretax store
    return (pretax_accounts as PretaxWallet[]).map((wallet) => {
      const wallet_type = translateWalletTypeToDisplay(
        wallet.wallet_type_id as PRETAX_ACCOUNT_V2_TYPES
      );

      // Sometimes it's possible to have 2+ active pretax accounts from different years
      const wallet_start_year =
        wallet.company_wallet_configuration &&
        date_helper(wallet.company_wallet_configuration.start_date).format(
          'YYYY'
        );

      const wallet_name = `${wallet_start_year} ${wallet_type}`;

      return (
        <SideLink
          key={wallet.id}
          active={Boolean(year && year === wallet_start_year)}
          route={StoreRoutes.detailPretax({
            pretax_account: wallet_type,
            year: wallet_start_year,
          })}
        >
          {wallet_name}
        </SideLink>
      );
    });
  };

  return (
    <Container>
      <Section>
        <AccountContainer>
          <SideNavigationBar header="Browse by Account">
            {employee_wallets
              .filter(
                ({ company_wallet_configuration: { is_twic_store_enabled } }) =>
                  is_twic_store_enabled
              )
              .map(({ id, company_wallet_configuration: { wallet_name } }) => (
                <SideLink
                  key={id}
                  route={StoreRoutes.listFeatured({
                    employee_wallet_id: id,
                    featured_section: wallet_name,
                  })}
                >
                  {getWalletDisplayName(wallet_name)}
                </SideLink>
              ))}
          </SideNavigationBar>
          {pretax_accounts.length > 0 && (
            <PreTaxAccountContainer>
              <SideNavigationBar>{renderSideLinks()}</SideNavigationBar>
            </PreTaxAccountContainer>
          )}
        </AccountContainer>
      </Section>
      {
        // when there is no wallet set, this whole section should be hidden
        // https://linear.app/forma/issue/PRT-998
        filtered_wallet && company_wallet_configuration && (
          <>
            <HorizontalSeparator>
              <Divider />
            </HorizontalSeparator>
            <CategoryContainer
              data-testid={STORE_LAYOUT_ELEMENTS.SUBCATEGORY_LINK}
            >
              <SideNavigationBar header="Browse by Category">
                {orderBy(
                  categories_with_vendors,
                  [
                    (categories) =>
                      categories.alias
                        ? categories.alias.toLowerCase()
                        : categories.name.toLowerCase(),
                  ],
                  'asc'
                ).map(({ name, id, alias }) => (
                  <SideLink
                    key={id}
                    category_id={id}
                    route={StoreRoutes.listCategory({
                      employee_wallet_id: filtered_wallet.id,
                      category_id: id,
                      alias: alias ? alias : id,
                    })}
                  >
                    {alias ? startCase(alias) : startCase(name)}
                  </SideLink>
                ))}
              </SideNavigationBar>
            </CategoryContainer>
          </>
        )
      }
      <HorizontalSeparator>
        <Divider />
      </HorizontalSeparator>
      <SuggestionContainer>
        <SuggestionQuestion>{`Can't find something?`}</SuggestionQuestion>
        <FormLinkContainer>
          <FormLink href="https://twic.typeform.com/to/QjUpdaVG">
            <FormLinkText>Submit a request</FormLinkText>
          </FormLink>
          <FormText>
            {` to the Forma team and tell us what you'd love to see that's currently missing.`}
          </FormText>
        </FormLinkContainer>
      </SuggestionContainer>
    </Container>
  );
};

export default CategoryFilters;
